import { useRef, useCallback, useLayoutEffect } from "react"
import { Option } from "./types"

type UseListboxScrollTopArgs = {
  preserveListboxScrollTop?: boolean
  open?: boolean
  valueOption?: Option | Option[]
  optionsLength?: number
}

export const useListboxScrollTop = (args: UseListboxScrollTopArgs) => {
  const { preserveListboxScrollTop, open, valueOption, optionsLength } = args

  // DOM ref to the listbox <ul> so we can read/write its scrollTop.
  const listboxRef = useRef<HTMLUListElement | null>(null)
  // Stores last scrollTop across re-renders without triggering updates.
  const lastKnownScrollTopRef = useRef<number>(0)
  // Prevents restoring scrollTop before the user actually scrolls.
  const hasUserScrolledRef = useRef<boolean>(false)

  const restoreScrollTop = useCallback(() => {
    if (!open || !preserveListboxScrollTop || !hasUserScrolledRef.current) {
      return
    }

    const el = listboxRef.current
    if (!el) return

    // Clamp because list height may change after selection toggles / option list updates.
    const maxScrollTop = el.scrollHeight - el.clientHeight
    const clamped = Math.max(
      0,
      Math.min(lastKnownScrollTopRef.current, maxScrollTop)
    )

    // Apply immediately (layout) to prevent visible jump.
    if (el.scrollTop !== clamped) {
      el.scrollTop = clamped
    }

    requestAnimationFrame(() => {
      const node = listboxRef.current
      if (!node) return

      // only force-reset when the shift is noticeable (>= 2px) to avoid micro "jitter"
      const delta = Math.abs(node.scrollTop - clamped)

      if (delta >= 2) {
        node.scrollTop = clamped
      }
    })
  }, [open, preserveListboxScrollTop])

  // Remember the user's current scrollTop so we can restore it after re-render.
  const handleListboxScroll = useCallback(
    (event: React.UIEvent) => {
      if (!preserveListboxScrollTop) return
      const el = event.currentTarget as HTMLUListElement
      lastKnownScrollTopRef.current = el.scrollTop
      hasUserScrolledRef.current = true
    },
    [preserveListboxScrollTop]
  )

  const setListboxRef = useCallback(
    (node: HTMLUListElement | null) => {
      listboxRef.current = node
      // If the listbox gets re-mounted (option list changes), immediately restore.
      if (node) {
        restoreScrollTop()
      }
    },
    [restoreScrollTop]
  )

  // Restore list scrollTop after option/value changes (prevents jump to the top).
  useLayoutEffect(() => {
    restoreScrollTop()
  }, [
    open,
    valueOption,
    optionsLength,
    preserveListboxScrollTop,
    restoreScrollTop
  ])

  return {
    listboxRef: setListboxRef,
    handleListboxScroll
  }
}
